import { useEffect, useRef, useState } from "react";
import { Pin } from "lucide-react";
import type { Question, Response } from "../lib/types";
import { AnswerDisplay } from "./AnswerDisplay";

interface Props {
  responses: Response[];
  /** When present, typed-question answers (NPS rating, MCQ option, polar side) are
   *  rendered alongside the free-text "why". Omit for open-text-only contexts. */
  question?: Question | null;
  /** Called once per response with its rendered card element (or `null` on unmount). */
  registerCardRef?: (responseId: string, el: HTMLElement | null) => void;
}

/** Below this count the stage runs poster mode: few answers own the whole
 *  column as large centered cards. Above it, the denser column flow takes over. */
const POSTER_MODE_MAX = 8;

/** Column-flow cap: newest cards render, older ones roll into the "+N weitere"
 *  counter so the wall never becomes an unreadable archive. */
const FLOW_VISIBLE_MAX = 40;

/** How long the newest answer holds its spotlight when no newer one arrives. */
const SPOTLIGHT_HOLD_MS = 8000;

/** Deterministic Störer-style tilt per response: stable across re-renders,
 *  between -2° and +2°, never 0 for odd hashes so the room reads it as placed
 *  by hand rather than misrendered. */
function rotationFor(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return ((Math.abs(h) % 9) - 4) / 2; // -2 .. +2 in 0.5 steps
}

export default function ResponseTicker({ responses, question, registerCardRef }: Props) {
  const visible = responses.filter((r) => !r.is_hidden);
  const [spotlightId, setSpotlightId] = useState<string | null>(null);
  const prevIdsRef = useRef<Set<string>>(new Set());
  const spotlightTimerRef = useRef<number | null>(null);

  // The newest arrival HOLDS a spotlight state (lime shadow + Neu chip) until
  // the next answer lands or the hold expires. This is the participant's
  // "my words are on the wall" moment — it must survive looking up late.
  // Freshness window instead of a mount flag: useResponses streams the
  // initial data in AFTER mount, so only answers submitted moments ago count
  // as live arrivals (same invariant as the flight-animation FRESHNESS_MS).
  useEffect(() => {
    const incoming = visible.filter((r) => !prevIdsRef.current.has(r.id));
    prevIdsRef.current = new Set(visible.map((r) => r.id));
    if (incoming.length === 0) return;

    const newest = incoming[incoming.length - 1];
    const submittedMs = newest.submitted_at ? Date.parse(newest.submitted_at) : 0;
    if (!(submittedMs > 0 && Date.now() - submittedMs < 30_000)) return;
    setSpotlightId(newest.id);
    if (spotlightTimerRef.current != null) window.clearTimeout(spotlightTimerRef.current);
    spotlightTimerRef.current = window.setTimeout(() => {
      setSpotlightId(null);
      spotlightTimerRef.current = null;
    }, SPOTLIGHT_HOLD_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible.length]);

  useEffect(() => {
    return () => {
      if (spotlightTimerRef.current != null) window.clearTimeout(spotlightTimerRef.current);
    };
  }, []);

  if (visible.length === 0) {
    return <EmptyLiveState />;
  }

  if (visible.length <= POSTER_MODE_MAX) {
    return (
      <PosterMode
        visible={visible}
        question={question ?? null}
        spotlightId={spotlightId}
        registerCardRef={registerCardRef}
      />
    );
  }

  return (
    <FlowMode
      visible={visible}
      question={question ?? null}
      spotlightId={spotlightId}
      registerCardRef={registerCardRef}
    />
  );
}

/** n = 0 — the wall says "live and listening", not "black void". */
function EmptyLiveState() {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-5">
      <span className="relative inline-flex w-2.5 h-2.5">
        <span className="absolute inset-0 rounded-full bg-rc-lime opacity-70 animate-shimmer" />
        <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-rc-lime" />
      </span>
      <p
        className="text-rc-stone font-semibold tracking-[-0.01em]"
        style={{ fontSize: "clamp(1.1rem, 1.8vw, 1.5rem)" }}
      >
        Antworten erscheinen hier live
      </p>
    </div>
  );
}

/** n ≤ POSTER_MODE_MAX — few answers own the stage as large centered cards. */
function PosterMode({
  visible,
  question,
  spotlightId,
  registerCardRef,
}: {
  visible: Response[];
  question: Question | null;
  spotlightId: string | null;
  registerCardRef?: (responseId: string, el: HTMLElement | null) => void;
}) {
  const n = visible.length;
  // Display-scale type that steps down as the stage fills.
  const fontSize =
    n <= 3
      ? "clamp(1.5rem, 2.6vw, 2.5rem)"
      : "clamp(1.15rem, 1.9vw, 1.8rem)";

  return (
    <div className="h-full overflow-hidden flex flex-wrap content-center justify-center items-center gap-x-8 gap-y-9 px-4">
      {visible.map((r) => (
        <ResponseCard
          key={r.id}
          response={r}
          question={question}
          spotlit={spotlightId === r.id}
          registerCardRef={registerCardRef}
          fontSize={fontSize}
          maxWidth="34ch"
          rotate
        />
      ))}
    </div>
  );
}

/** n > POSTER_MODE_MAX — newest-first column flow, capped with a counter. */
function FlowMode({
  visible,
  question,
  spotlightId,
  registerCardRef,
}: {
  visible: Response[];
  question: Question | null;
  spotlightId: string | null;
  registerCardRef?: (responseId: string, el: HTMLElement | null) => void;
}) {
  // Newest first: growth happens at a stable, watchable position (top-left),
  // pinned voices stay ahead of the stream.
  const pinned = visible.filter((r) => r.is_pinned);
  const rest = visible.filter((r) => !r.is_pinned).reverse();
  const shown = rest.slice(0, FLOW_VISIBLE_MAX);
  const overflow = rest.length - shown.length;

  return (
    <div className="h-full overflow-hidden flex flex-col gap-4">
      {/* pt-3 gives the spotlight card's Neu chip headroom inside the clipped container. */}
      <div className="columns-2 2xl:columns-3 gap-4 flex-1 min-h-0 overflow-hidden pt-3">
        {[...pinned, ...shown].map((r) => (
          <div key={r.id} className="break-inside-avoid mb-4">
            <ResponseCard
              response={r}
              question={question}
              spotlit={spotlightId === r.id}
              registerCardRef={registerCardRef}
              fontSize="clamp(1.05rem, 1.4vw, 1.35rem)"
              maxWidth="100%"
            />
          </div>
        ))}
      </div>
      {overflow > 0 && (
        <p className="flex-shrink-0 text-rc-lime font-mono font-semibold text-[13px] tracking-[0.08em] tabular-nums">
          +{overflow} weitere Antworten
        </p>
      )}
    </div>
  );
}

/**
 * One voice on the wall. Solid Paper card with Ink text (maximum projector
 * contrast), hard offset shadow, slight deterministic tilt in poster mode.
 * Pinned voices keep the Berry treatment; the newest arrival wears the Lime
 * spotlight with a Neu chip until the next one lands.
 */
function ResponseCard({
  response,
  question,
  spotlit,
  registerCardRef,
  fontSize,
  maxWidth,
  rotate = false,
}: {
  response: Response;
  question: Question | null;
  spotlit: boolean;
  registerCardRef?: (responseId: string, el: HTMLElement | null) => void;
  fontSize: string;
  maxWidth: string;
  rotate?: boolean;
}) {
  const isPinned = response.is_pinned;
  const rotation = rotate ? rotationFor(response.id) : 0;

  const surfaceCls = isPinned
    ? "bg-rc-berry text-rc-paper shadow-hard"
    : spotlit
      ? "bg-rc-paper text-rc-black shadow-hard-lime border-2 border-rc-lime"
      : "bg-rc-paper text-rc-black shadow-hard";

  return (
    <article
      ref={(el) => {
        registerCardRef?.(response.id, el);
      }}
      className={`relative rounded-rc-lg px-6 py-5 animate-fade-up transition-shadow duration-500 ${surfaceCls}`}
      style={{
        fontSize,
        lineHeight: 1.4,
        maxWidth,
        transform: rotation !== 0 ? `rotate(${rotation}deg)` : undefined,
      }}
    >
      {spotlit && !isPinned && (
        <span
          className="absolute -top-3 -right-3 bg-rc-lime text-rc-black font-bold text-[11px] tracking-[0.14em] uppercase rounded-pill px-2.5 py-1 border-2 border-rc-black"
          style={{ transform: "rotate(6deg)" }}
        >
          Neu
        </span>
      )}
      {isPinned && (
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-rc-paper text-rc-berry float-left mr-3 mt-0.5">
          <Pin className="w-3.5 h-3.5" />
        </span>
      )}
      <div className="font-semibold tracking-[-0.01em] [overflow-wrap:anywhere]">
        <AnswerDisplay
          question={question}
          response={response}
          surface={isPinned ? "dark" : "light"}
        />
      </div>
    </article>
  );
}
